import type { Column } from "@shared/types/data-table"
import type { Terminal } from "@features/in-stores/types/terminal.types"
import type { ColumnManagementConfig } from "@shared/lib/tables/utils/column-management"
import { 
  createStatusColumn, 
  createDateColumn, 
  createTextColumn, 
  createCurrencyColumn, 
  createLocationColumn,
  createCustomColumn
} from "./column-factories"

export const TERMINAL_COLUMNS: Record<string, Column<Terminal>> = {
  name: createTextColumn<Terminal>("name", "Name", 180, "font-medium"),
  status: createStatusColumn<Terminal>("terminal"),
  tid: createTextColumn<Terminal>("tid", "TID", 120, "text-muted-foreground font-mono text-xs"),
  model: createTextColumn<Terminal>("model", "Model", 150),
  type: createTextColumn<Terminal>("type", "Type", 120, "text-muted-foreground"),
  store: createTextColumn<Terminal>("store", "Store", 150),
  location: createLocationColumn<Terminal>(180),
  ltv: createCurrencyColumn<Terminal>("ltv", "LTV", 100),
  lastSeen: createDateColumn<Terminal>("lastSeen", "Last seen", 130),
  createdAt: createDateColumn<Terminal>("createdAt", "Created", 130),
  lastUpdated: createDateColumn<Terminal>("lastUpdated", "Last updated", 130),
  blocked: createCustomColumn<Terminal>(
    "blocked",
    "Blocked",
    80,
    (terminal: Terminal) => (
      <span className={terminal.blocked ? "text-red-600" : "text-muted-foreground"}>
        {terminal.blocked ? "Yes" : "No"}
      </span>
    )
  ),
}

export const TERMINAL_STATUS_CONFIG = {
  all: ["name", "status", "tid", "model", "type", "store", "location", "ltv", "lastSeen", "createdAt", "lastUpdated", "blocked"],
  online: ["name", "status", "tid", "model", "type", "store", "location", "ltv", "lastSeen", "createdAt", "lastUpdated"],
  offline: ["name", "status", "tid", "model", "type", "store", "location", "lastSeen", "createdAt", "lastUpdated", "blocked"],
  error: ["name", "status", "tid", "model", "type", "store", "location", "lastSeen", "createdAt", "lastUpdated", "blocked"],
  maintenance: ["name", "status", "tid", "model", "type", "store", "location", "lastSeen", "createdAt", "lastUpdated"],
} as const

export const getTerminalColumns = (status: string = 'all'): Column<Terminal>[] => {
  const keys = TERMINAL_STATUS_CONFIG[status as keyof typeof TERMINAL_STATUS_CONFIG] || TERMINAL_STATUS_CONFIG.all
  return keys.map(key => TERMINAL_COLUMNS[key]).filter(Boolean)
}

export const getTerminalColumnConfig = (status: string = 'all'): ColumnManagementConfig[] => {
  const visibleKeys: Set<string> = new Set(TERMINAL_STATUS_CONFIG[status as keyof typeof TERMINAL_STATUS_CONFIG] || TERMINAL_STATUS_CONFIG.all)
  return Object.entries(TERMINAL_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key),
    required: ["name", "status"].includes(key),
    order: index
  }))
}